import { useCallback, useMemo, useRef } from "react";
import { Loader2, Wifi } from "lucide-react";

import { MwHeader } from "@/components/mediaweb/MwHeader";
import { SectionTitle } from "@/components/SectionTitle";
import { RadioCard } from "@/components/RadioCard";
import { Player } from "@/components/Player";
import { CtaPro, Footer } from "@/components/Footer";

import { useRadios } from "@/hooks/useRadios";
import { useMetadataPolling } from "@/hooks/useMetadataPolling";
import { useRadioPlayer } from "@/hooks/useRadioPlayer";
import { useMediaSession } from "@/hooks/useMediaSession";
import { useGridFillers } from "@/hooks/useGridFillers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 10000;

export default function WallPage() {
  const { radios } = useRadios(API);
  const metaMap = useMetadataPolling(API, POLL_INTERVAL_MS);
  const {
    audioRef,
    currentId,
    isPlaying,
    isLoading,
    volume,
    muted,
    setVolume,
    setMuted,
    setIsPlaying,
    setIsLoading,
    playRadio,
    togglePlay,
  } = useRadioPlayer();

  const playableRadios = useMemo(
    () => radios.filter((r) => !r.placeholder),
    [radios]
  );
  const stationsCount = playableRadios.length;
  const currentRadio = radios.find((r) => r.id === currentId);
  const currentMeta = currentId ? metaMap[currentId] : null;

  const gridRef = useRef(null);
  const fillersCount = useGridFillers(gridRef, radios.length);
  const fillerSlots = useMemo(
    () =>
      Array.from({ length: fillersCount }, (_, i) => ({
        id: `slot-libre-${i}`,
        name: "VOTRE RADIO ICI",
        tagline: "Slot disponible",
        logo: "",
        color: "#1a1a1d",
        placeholder: true,
      })),
    [fillersCount]
  );

  const zapNext = useCallback(() => {
    if (playableRadios.length === 0) return;
    const i = playableRadios.findIndex((r) => r.id === currentId);
    playRadio(playableRadios[(i + 1) % playableRadios.length]);
  }, [playableRadios, currentId, playRadio]);

  const zapPrev = useCallback(() => {
    if (playableRadios.length === 0) return;
    const i = playableRadios.findIndex((r) => r.id === currentId);
    const len = playableRadios.length;
    playRadio(playableRadios[(i - 1 + len) % len]);
  }, [playableRadios, currentId, playRadio]);

  useMediaSession({
    audioRef,
    currentId,
    radios,
    metaMap,
    isPlaying,
    onZapNext: zapNext,
    onZapPrev: zapPrev,
  });

  const handleVolume = useCallback(
    (v) => {
      setVolume(v);
      if (v > 0 && muted) setMuted(false);
    },
    [muted, setVolume, setMuted]
  );

  return (
    <div className="rh-app">
      <audio
        ref={audioRef}
        preload="none"
        playsInline
        onPlaying={() => {
          setIsPlaying(true);
          setIsLoading(false);
        }}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsLoading(true)}
        onCanPlay={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setIsPlaying(false);
        }}
        data-testid="radio-audio-element"
      />

      <MwHeader />

      <main className="rh-main rh-main-routed">
        <div className="rh-wall-banner" data-testid="wall-live-banner">
          <div className="rh-wall-banner-inner">
            <div className="rh-wall-banner-title">
              <span className="rh-wall-banner-dot" aria-hidden="true" />
              <span>WallTunes</span>
              <span className="rh-wall-banner-sep">·</span>
              <span className="rh-wall-banner-sub">Le mur de vos radios</span>
            </div>
            <div className="rh-wall-banner-meta">
              <Wifi size={13} />
              <span>{stationsCount} stations live</span>
            </div>
          </div>
        </div>

        <SectionTitle />
        <div className="rh-grid" data-testid="radio-wall-grid" ref={gridRef}>
          {radios.map((radio) => (
            <RadioCard
              key={radio.id}
              radio={radio}
              meta={metaMap[radio.id]}
              isActive={currentId === radio.id}
              isPlaying={currentId === radio.id && isPlaying}
              isLoading={currentId === radio.id && isLoading}
              onClick={() => playRadio(radio)}
            />
          ))}
          {fillerSlots.map((slot) => (
            <RadioCard
              key={slot.id}
              radio={slot}
              meta={null}
              isActive={false}
              isPlaying={false}
              isLoading={false}
              onClick={() => {}}
            />
          ))}
          {radios.length === 0 && (
            <div className="rh-empty">
              <Loader2 className="rh-spin" size={24} />
              <span>Chargement du mur de radios…</span>
            </div>
          )}
        </div>

        <CtaPro />
        <Footer />
      </main>

      <Player
        radio={currentRadio}
        meta={currentMeta}
        isPlaying={isPlaying}
        isLoading={isLoading}
        volume={volume}
        muted={muted}
        onTogglePlay={togglePlay}
        onZap={zapNext}
        onVolume={handleVolume}
        onMute={() => setMuted((m) => !m)}
      />
    </div>
  );
}
